/**
 * Quick date-range presets for filtering by transaction date.
 *
 * All ranges are computed client-side in local time (no UTC conversion),
 * so `formatLocalDate` deliberately avoids `Date#toISOString`, which
 * formats in UTC and can shift the calendar date near local midnight.
 */

const pad = (n) => String(n).padStart(2, '0');

/** Format a Date as YYYY-MM-DD using its local (not UTC) fields. */
export const formatLocalDate = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const startOfMonth = (year, month) => new Date(year, month, 1);
// Day 0 of the following month is the last day of `month`.
const endOfMonth = (year, month) => new Date(year, month + 1, 0);

/**
 * Build the list of quick date-range presets relative to `now`.
 * `now` is injectable for deterministic testing of boundary conditions
 * (e.g. the last day of a month, or a date in Q4).
 *
 * @param {Date} now
 * @returns {Array<{label: string, start: string, end: string}>}
 */
export const getQuickDateRanges = (now = new Date()) => {
  const year = now.getFullYear();
  const month = now.getMonth();

  const thisMonthStart = startOfMonth(year, month);
  const thisMonthEnd = endOfMonth(year, month);

  // Subtracting 1 from month and letting Date normalize handles the
  // January -> previous December rollover correctly.
  const lastMonthStart = startOfMonth(year, month - 1);
  const lastMonthEnd = endOfMonth(year, month - 1);

  const thisYearStart = new Date(year, 0, 1);
  const thisYearEnd = new Date(year, 11, 31);

  const lastYearStart = new Date(year - 1, 0, 1);
  const lastYearEnd = new Date(year - 1, 11, 31);

  const quarters = [1, 2, 3, 4].map((quarter) => {
    const startMonth = (quarter - 1) * 3;
    return {
      label: `Q${quarter}`,
      start: formatLocalDate(new Date(year, startMonth, 1)),
      end: formatLocalDate(new Date(year, startMonth + 3, 0))
    };
  });

  return [
    { label: 'This Month', start: formatLocalDate(thisMonthStart), end: formatLocalDate(thisMonthEnd) },
    { label: 'Last Month', start: formatLocalDate(lastMonthStart), end: formatLocalDate(lastMonthEnd) },
    { label: 'This Year', start: formatLocalDate(thisYearStart), end: formatLocalDate(thisYearEnd) },
    { label: 'Last Year', start: formatLocalDate(lastYearStart), end: formatLocalDate(lastYearEnd) },
    ...quarters
  ];
};
